import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowUpRight, Mail, Phone, Globe, MapPin } from "lucide-react";
import Reveal from "./Reveal";
import { MANAGING_PARTNER, OF_COUNSEL } from "../data/content";

function Chips({ items, max }) {
  const shown = max ? items.slice(0, max) : items;
  const extra = max ? items.length - shown.length : 0;
  return (
    <div className="mt-5 flex flex-wrap gap-2">
      {shown.map((p) => (
        <span key={p} className="text-xs font-500 px-3 py-1 rounded-full border border-white/15 text-[#94A3B8]">
          {p}
        </span>
      ))}
      {extra > 0 && (
        <span className="text-xs font-700 px-3 py-1 rounded-full border border-[#D95A41]/40 text-[#D95A41]">
          +{extra} more
        </span>
      )}
    </div>
  );
}

function Avatar({ person, size = "w-16 h-16" }) {
  if (person.photo) {
    return <img src={person.photo} alt={person.name} className={`${size} rounded-full object-cover object-top border border-[#D95A41]/40`} />;
  }
  return (
    <div className={`${size} rounded-full border border-[#D95A41]/50 bg-[#050A14] flex items-center justify-center shrink-0`}>
      <span className="text-xl font-900 tracking-tight text-[#D95A41]">{person.initials}</span>
    </div>
  );
}

function ProfileModal({ person, onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [onClose]);

  return createPortal(
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] bg-[#050A14]/90 backdrop-blur-md flex items-center justify-center p-4 sm:p-6"
      onClick={onClose}
      data-testid="team-modal"
    >
      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.98 }}
        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-3xl border border-white/10 bg-gradient-to-br from-[#0B132B] to-[#050A14] p-8 md:p-10"
      >
        <button onClick={onClose} data-testid="team-modal-close" aria-label="Close"
          className="absolute top-5 right-5 text-[#94A3B8] hover:text-[#D95A41] transition-colors">
          <X size={26} />
        </button>

        <div className="flex items-center gap-5">
          <Avatar person={person} size="w-20 h-20" />
          <div>
            <span className="text-xs font-700 tracking-[0.2em] uppercase text-[#D95A41]">{person.role}</span>
            <h3 className="text-2xl md:text-3xl font-900 tracking-tightest text-[#F2EFE9] mt-1">{person.name}</h3>
            {person.firm && <p className="text-sm text-[#94A3B8]">{person.firm}</p>}
          </div>
        </div>
        <p className="mt-3 text-sm text-[#94A3B8]/90">{person.credential}</p>

        <div className="mt-6 space-y-4">
          {person.fullBio.map((para, i) => (
            <p key={i} className="text-[#94A3B8] leading-relaxed">{para}</p>
          ))}
        </div>

        <p className="mt-8 text-xs font-700 tracking-[0.2em] uppercase text-[#94A3B8]">Practice Areas</p>
        <Chips items={person.practiceAreas} />

        {(person.email || person.phone || person.website || person.address) && (
          <>
            <p className="mt-8 text-xs font-700 tracking-[0.2em] uppercase text-[#94A3B8]">Get in touch</p>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="team-modal-contact">
              {person.email && (
                <a
                  href={`mailto:${person.email}`}
                  className="flex items-center gap-3 rounded-xl border border-white/10 bg-[#050A14]/40 px-4 py-3 hover:border-[#D95A41]/50 transition-colors text-sm text-[#F2EFE9]/90"
                  data-testid="team-modal-email"
                >
                  <Mail size={16} className="text-[#D95A41] flex-shrink-0" />
                  <span className="truncate" style={{ textAlign: "left" }}>{person.email}</span>
                </a>
              )}
              {person.phone && (
                <a
                  href={`tel:${person.phone.replace(/\s+/g, "")}`}
                  className="flex items-center gap-3 rounded-xl border border-white/10 bg-[#050A14]/40 px-4 py-3 hover:border-[#D95A41]/50 transition-colors text-sm text-[#F2EFE9]/90"
                  data-testid="team-modal-phone"
                >
                  <Phone size={16} className="text-[#D95A41] flex-shrink-0" />
                  <span style={{ textAlign: "left" }}>{person.phone}</span>
                </a>
              )}
              {person.website && (
                <a
                  href={person.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 rounded-xl border border-white/10 bg-[#050A14]/40 px-4 py-3 hover:border-[#D95A41]/50 transition-colors text-sm text-[#F2EFE9]/90"
                  data-testid="team-modal-website"
                >
                  <Globe size={16} className="text-[#D95A41] flex-shrink-0" />
                  <span className="truncate" style={{ textAlign: "left" }}>{person.website.replace(/^https?:\/\//, "")}</span>
                </a>
              )}
              {person.address && (
                <div
                  className="flex items-start gap-3 rounded-xl border border-white/10 bg-[#050A14]/40 px-4 py-3 text-sm text-[#F2EFE9]/90"
                  data-testid="team-modal-address"
                >
                  <MapPin size={16} className="text-[#D95A41] flex-shrink-0 mt-0.5" />
                  <span style={{ textAlign: "left" }}>{person.address}</span>
                </div>
              )}
            </div>
          </>
        )}
      </motion.div>
    </motion.div>,
    document.body
  );
}

export default function Team() {
  const [active, setActive] = useState(null);
  const mp = MANAGING_PARTNER;

  const open = useCallback((p) => setActive(p), []);

  return (
    <section id="team" className="relative z-10 py-28 md:py-40 max-w-[1400px] mx-auto px-6 lg:px-12" data-testid="team">
      <Reveal>
        <p className="text-sm font-500 tracking-[0.3em] uppercase text-[#D95A41] mb-6">Meet the Team</p>
        <p className="max-w-2xl text-lg text-[#94A3B8] leading-relaxed">
          A core team backed by specialist Of-Counsel across litigation, aviation finance and disputes — the right expertise on every mandate.
        </p>
      </Reveal>

      {/* Managing Partner */}
      <Reveal delay={0.05}>
        <button
          onClick={() => open(mp)}
          data-testid="team-managing-partner"
          className="mt-14 w-full text-left rounded-3xl border border-white/10 hover:border-[#D95A41]/40 bg-gradient-to-br from-[#0B132B] to-[#050A14] overflow-hidden grid grid-cols-1 lg:grid-cols-12 transition-colors group"
        >
          <div className="lg:col-span-4 relative min-h-[340px]">
            <img src={mp.photo} alt={mp.name} className="absolute inset-0 w-full h-full object-contain object-center bg-[#0B132B]" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050A14]/70 to-transparent lg:bg-gradient-to-r" />
          </div>
          <div className="lg:col-span-8 p-8 md:p-12">
            <span className="text-xs font-700 tracking-[0.2em] uppercase text-[#D95A41]">{mp.role}</span>
            <h3 className="mt-3 text-3xl font-900 tracking-tightest text-[#F2EFE9]">{mp.name}</h3>
            <p className="text-sm text-[#94A3B8] mt-1">{mp.credential}</p>
            <p className="mt-5 text-[#94A3B8] leading-relaxed">{mp.intro}</p>
            <Chips items={mp.practiceAreas} max={4} />
            <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-700 text-[#D95A41] group-hover:gap-2.5 transition-all">
              View full profile <ArrowUpRight size={16} />
            </span>
          </div>
        </button>
      </Reveal>

      {/* Of-Counsel */}
      <Reveal delay={0.1}>
        <p className="mt-16 mb-6 text-xs font-700 tracking-[0.25em] uppercase text-[#94A3B8]">Of-Counsel</p>
      </Reveal>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {OF_COUNSEL.map((person, i) => (
          <Reveal key={person.name} delay={0.1 + i * 0.08}>
            <motion.button
              onClick={() => open(person)}
              whileHover={{ y: -4 }}
              transition={{ duration: 0.3 }}
              className="h-full w-full text-left rounded-2xl border border-white/10 bg-[#0B132B]/50 hover:border-[#D95A41]/40 transition-colors p-8 flex flex-col group"
              data-testid={`of-counsel-${i}`}
            >
              <div className="flex items-center gap-4">
                <Avatar person={person} size="w-14 h-14" />
                <div>
                  <h3 className="text-lg font-700 text-[#F2EFE9] leading-tight">{person.name}</h3>
                  <p className="text-xs font-700 tracking-[0.15em] uppercase text-[#D95A41]">{person.role}</p>
                </div>
              </div>
              <p className="mt-4 text-xs text-[#94A3B8]/80">{person.credential}</p>
              <p className="mt-4 text-sm text-[#94A3B8] leading-relaxed">{person.intro}</p>
              <Chips items={person.practiceAreas} max={4} />
              <span className="mt-auto pt-6 inline-flex items-center gap-1.5 text-sm font-700 text-[#D95A41] group-hover:gap-2.5 transition-all">
                View full profile <ArrowUpRight size={16} />
              </span>
            </motion.button>
          </Reveal>
        ))}
      </div>

      <AnimatePresence>
        {active && <ProfileModal person={active} onClose={() => setActive(null)} />}
      </AnimatePresence>
    </section>
  );
}
